import * as React from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  DeleteReviewDocument,
  GetPerformanceReviewsDocument,
  PerformanceReviewsReview,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import TextField from 'modules/core/components/TextField'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface Props {
  review: PerformanceReviewsReview
  onClose: () => void
}

export default function DeleteReviewModal({ review, onClose }: Props) {
  const [inputName, setInputName] = React.useState<string>('')
  const name = review.name
  const inputIsOk = inputName.trim() === name.trim()

  const [deleteReview] = useMutation(DeleteReviewDocument, {
    extractorFn: (data) => data.performanceReviewsMutations.deletePerformancereviewsReview,
  })

  const { running, performAction: onSubmit } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      if (!inputIsOk) return

      const response = await deleteReview({
        awaitRefetchQueries: true,
        variables: {
          id: review.id,
        },
        refetchQueries: [
          {
            query: GetPerformanceReviewsDocument,
          },
        ],
      })

      const handled = handleResult(response, () => undefined)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('surveys.form.delete.review_deleted'),
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_error'),
        })
      }

      closePortal()

      return handled
    }
  )

  const disabled = running || !inputIsOk

  return (
    <Modal
      description={i18n.t('surveys.form.delete.review_description')}
      footer={
        <Button type="negative" disabled={disabled} submit>
          {i18n.t('surveys.form.delete.review_submit')}
        </Button>
      }
      illustration="surveys"
      onClose={onClose}
      onSubmit={onSubmit}
      title={i18n.t('surveys.form.delete.title', {
        name,
      })}
    >
      <Box flexDirection="column" gap="s8">
        <Text.Normal whiteSpace="breakSpaces">
          {i18n.t('surveys.form.delete.review_input_name', { name })}
        </Text.Normal>
        <TextField onChange={setInputName} value={inputName} />
      </Box>
    </Modal>
  )
}
